<div class="p-6 w-full">
  <!-- Header with Search and Add Button -->
  <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
    <div class="w-full md:w-auto">
      <h1 class="text-3xl font-bold text-gray-900">Company Admins</h1>
      <p class="text-gray-600">List of all company administrators with company logos</p>
    </div>
    
    <div class="w-full md:w-auto flex flex-col sm:flex-row gap-4">
      <!-- Search Bar with Dropdown -->
      <div class="relative w-full sm:w-64">
        <div class="relative">
          <mat-icon class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">search</mat-icon>
          <input type="text" 
                 [(ngModel)]="searchQuery"
                 (ngModelChange)="onSearchInput()"
                 (focus)="showSearchDropdown = filteredAdmins.length > 0 && searchQuery.trim() !== ''"
                 (blur)="onSearchBlur()"
                 placeholder="Search by email, name, or company..."
                 class="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white">
          
          <!-- Clear button -->
          <button *ngIf="searchQuery" 
                  (click)="clearSearch()"
                  class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <mat-icon class="text-sm">close</mat-icon>
          </button>
        </div>
        
        <!-- Search Dropdown -->
        <div *ngIf="showSearchDropdown && filteredAdmins.length > 0" 
             class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          <div class="p-2">
            <div class="text-xs font-semibold text-gray-500 px-3 py-2">Search Results ({{filteredAdmins.length}})</div>
            <div class="divide-y divide-gray-100">
              <div *ngFor="let admin of filteredAdmins" 
                   (click)="selectAdmin(admin)"
                   class="px-3 py-3 hover:bg-blue-50 cursor-pointer transition-colors duration-150">
                <div class="flex items-center">
                  <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span class="text-blue-600 font-bold text-xs">
                      {{admin.name?.charAt(0) || 'A'}}
                    </span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-gray-900 truncate">{{admin.name}}</div>
                    <div class="text-sm text-gray-500 truncate">{{admin.email}}</div>
                    <div class="text-xs text-gray-400 mt-1">
                      <span class="inline-flex items-center px-2 py-0.5 rounded bg-gray-100">
                        {{admin.company?.name || 'No Company'}}
                      </span>
                    </div>
                  </div>
                  <mat-icon class="text-gray-400 ml-2">chevron_right</mat-icon>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Selected Admin Badge -->
        <div *ngIf="selectedAdmin" class="mt-2">
          <div class="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm">
            <div class="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center mr-1">
              <span class="text-blue-600 font-bold text-xs">
                {{selectedAdmin.name?.charAt(0) || 'A'}}
              </span>
            </div>
            <span class="font-medium">{{selectedAdmin.name}}</span>
            <span class="mx-2">•</span>
            <span class="text-xs">{{selectedAdmin.company?.name || 'No Company'}}</span>
            <button (click)="clearSearch()" class="ml-2 text-blue-500 hover:text-blue-700">
              <mat-icon class="text-sm">close</mat-icon>
            </button>
          </div>
        </div>
      </div>
      
      <!-- Add Button -->
      <button mat-raised-button 
              color="primary" 
              (click)="openAddCompanyModal()"
              class="flex items-center whitespace-nowrap">
        <mat-icon>add</mat-icon>
        <span class="ml-1">Add New Company Admin</span>
      </button>
    </div>
  </div>

  <!-- Search Info Bar -->
  <div *ngIf="isSearchActive" class="mb-4">
    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <mat-icon class="text-blue-600 mr-2">info</mat-icon>
          <span class="text-blue-700">
            Showing search results for "{{searchQuery}}"
          </span>
        </div>
        <button mat-button 
                (click)="clearSearch()"
                class="text-blue-600 hover:text-blue-800">
          Clear Search
        </button>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="flex justify-center items-center p-12">
    <mat-progress-spinner mode="indeterminate" diameter="50"></mat-progress-spinner>
  </div>

  <!-- Error State -->
  <div *ngIf="errorMessage && !isLoading" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
    <div class="flex items-center">
      <mat-icon class="text-red-500 mr-2">error</mat-icon>
      <span class="text-red-700">{{errorMessage}}</span>
    </div>
    <button mat-stroked-button 
            color="warn" 
            (click)="loadCompanyAdmins()"
            class="mt-2">
      <mat-icon>refresh</mat-icon>
      <span class="ml-1">Retry</span>
    </button>
  </div>

  <!-- Company Admins Table - FULL WIDTH -->
  <div *ngIf="!isLoading && displayAdmins.length > 0" class="mb-8 w-full">
    <div class="bg-white rounded-lg shadow overflow-hidden w-full">
      <!-- Table Container -->
      <div class="overflow-x-auto w-full">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Logo
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Admin Details
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company Details
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created Date
              </th>
              <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <ng-container *ngFor="let admin of displayAdmins">
              <!-- Main Row -->
              <tr id="admin-{{admin._id}}" class="hover:bg-gray-50 cursor-pointer" (click)="toggleRow(admin._id)">
                <!-- Logo Column -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center justify-center">
                    <div *ngIf="hasLogo(admin)" class="relative group">
                      <img [src]="getLogoUrl(admin)" 
                           [alt]="admin.company?.name + ' logo'"
                           class="w-12 h-12 rounded-lg object-cover border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                      <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <mat-icon class="text-white text-opacity-80">zoom_in</mat-icon>
                      </div>
                    </div>
                    <div *ngIf="!hasLogo(admin)" 
                         class="w-12 h-12 rounded-lg bg-gray-100 border border-gray-300 flex items-center justify-center">
                      <mat-icon class="text-gray-400">business</mat-icon>
                    </div>
                  </div>
                </td>

                <!-- Admin Details Column -->
                <td class="px-6 py-4">
                  <div class="flex items-center">
                    <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mr-3 shadow-sm">
                      <span class="text-white font-bold text-sm">
                        {{admin.name?.charAt(0) || 'A'}}
                      </span>
                    </div>
                    <div class="min-w-0 flex-1">
                      <div class="font-semibold text-gray-900 truncate">{{admin.name || 'N/A'}}</div>
                      <div class="flex items-center mt-1">
                        <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                          {{admin.role || 'admin'}}
                        </span>
                        <span *ngIf="admin.profileType" 
                              [ngClass]="{
                                'bg-green-100 text-green-800': admin.profileType === 'public',
                                'bg-purple-100 text-purple-800': admin.profileType === 'private'
                              }"
                              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium">
                          {{admin.profileType}}
                        </span>
                        <span *ngIf="admin.accountStatus === 'invited'"
                              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 ml-2">
                          Invitation pending
                        </span>
                      </div>
                    </div>
                  </div>
                </td>

                <!-- Company Details Column -->
                <td class="px-6 py-4">
                  <div class="min-w-0 flex-1">
                    <div class="font-medium text-gray-900 truncate">
                      {{admin.company?.name || 'N/A'}}
                    </div>
                    <!-- Removed Company ID display -->
                  </div>
                </td>

                <!-- Created Date Column -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm">
                    <div class="text-gray-700">{{formatDate(admin.createdAt)}}</div>
                    <div class="text-xs text-gray-500 mt-1">
                      <mat-icon class="align-middle mr-1" style="font-size: 12px;">schedule</mat-icon>
                      {{admin.createdAt | date: 'shortTime'}}
                    </div>
                  </div>
                </td>

                <!-- Actions Column -->
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <div class="flex justify-end space-x-1">
                    <button mat-icon-button 
                            matTooltip="View Details"
                            class="text-blue-600 hover:bg-blue-50"
                            (click)="toggleRow(admin._id); $event.stopPropagation()">
                      <mat-icon *ngIf="expandedAdminId !== admin._id">expand_more</mat-icon>
                      <mat-icon *ngIf="expandedAdminId === admin._id">expand_less</mat-icon>
                    </button>
                    <button mat-icon-button 
                            [matMenuTriggerFor]="menu"
                            matTooltip="More Actions"
                            class="text-gray-600 hover:bg-gray-100"
                            (click)="$event.stopPropagation()">
                      <mat-icon>more_vert</mat-icon>
                    </button>
                    <mat-menu #menu="matMenu">
                      <button *ngIf="admin.accountStatus === 'invited'" mat-menu-item
                              [disabled]="invitationActionId === admin._id"
                              (click)="resendInvitation(admin, $event)">
                        <mat-icon>forward_to_inbox</mat-icon>
                        <span>Resend invitation</span>
                      </button>
                      <button mat-menu-item 
                              *ngIf="admin.accountStatus !== 'invited'"
                              class="text-blue-600"
                              (click)="editAdmin(admin, $event)">
                        <mat-icon>edit</mat-icon>
                        <span>Edit Admin</span>
                      </button>
                      <button *ngIf="canPerformDestructiveActions" mat-menu-item
                              class="text-red-600"
                              (click)="openDeleteModal(admin, $event)">
                        <mat-icon>delete</mat-icon>
                        <span>{{admin.accountStatus === 'invited' ? 'Revoke invitation' : 'Delete Admin'}}</span>
                      </button>
                    </mat-menu>
                  </div>
                </td>
              </tr>

              <!-- Expanded Details Row -->
              <tr *ngIf="expandedAdminId === admin._id" class="bg-blue-50">
                <td colspan="5" class="px-6 py-4">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Admin Details -->
                    <div class="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                      <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <mat-icon class="mr-2 text-blue-600">person</mat-icon>
                        Admin Details
                      </h3>
                      <div class="space-y-3">
                        <!-- Removed ID display -->
                        <div class="flex items-center">
                          <mat-icon class="mr-2 text-gray-400">email</mat-icon>
                          <span class="text-sm font-medium text-gray-600 mr-2">Email:</span>
                          <a [href]="'mailto:' + admin.email" 
                             class="text-blue-600 hover:text-blue-800 hover:underline">
                            {{admin.email}}
                          </a>
                        </div>
                        <div class="flex items-center">
                          <mat-icon class="mr-2 text-gray-400">verified</mat-icon>
                          <span class="text-sm font-medium text-gray-600 mr-2">Email Verified:</span>
                          <span [class]="admin.isEmailVerified ? 'text-green-600' : 'text-yellow-600'" 
                                class="text-sm font-medium">
                            {{admin.isEmailVerified ? 'Yes ✓' : 'No ✗'}}
                          </span>
                        </div>
                        <div class="flex items-center">
                          <mat-icon class="mr-2 text-gray-400">phone</mat-icon>
                          <span class="text-sm font-medium text-gray-600 mr-2">Phone Verified:</span>
                          <span [class]="admin.isPhoneVerified ? 'text-green-600' : 'text-yellow-600'" 
                                class="text-sm font-medium">
                            {{admin.isPhoneVerified ? 'Yes ✓' : 'No ✗'}}
                          </span>
                        </div>
                        <div class="flex items-center">
                          <mat-icon class="mr-2 text-gray-400">language</mat-icon>
                          <span class="text-sm font-medium text-gray-600 mr-2">Language:</span>
                          <span class="text-sm">{{admin.language || 'en'}}</span>
                        </div>
                        <div *ngIf="admin.lastUpdatedPasswordTimeStamp" class="flex items-center">
                          <mat-icon class="mr-2 text-gray-400">lock_clock</mat-icon>
                          <span class="text-sm font-medium text-gray-600 mr-2">Password Updated:</span>
                          <span class="text-sm">{{formatDateTime(admin.lastUpdatedPasswordTimeStamp)}}</span>
                        </div>
                      </div>
                    </div>

                    <app-company-details-card
                      [company]="admin.company"
                      [logoUrl]="hasLogo(admin) ? getLogoUrl(admin) : null" />
                  </div>
                </td>
              </tr>
            </ng-container>
          </tbody>
        </table>
      </div>

      <!-- Table Footer with Pagination -->
      <div class="bg-gray-50 px-6 py-3 border-t border-gray-200">
        <div class="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
          <div class="text-sm text-gray-700">
            Showing <span class="font-medium">{{displayAdmins.length}}</span> of 
            <span class="font-medium">{{pagination.totalResults}}</span> company admins
            <span *ngIf="isSearchActive" class="text-blue-600 ml-2">
              (Search Results)
            </span>
          </div>
          
          <!-- Pagination Controls -->
          <div class="flex items-center space-x-2">
            <button mat-button 
                    (click)="prevPage()"
                    [disabled]="pagination.page <= 1"
                    class="text-blue-600 hover:text-blue-800 disabled:text-gray-400">
              <mat-icon>chevron_left</mat-icon>
              <span>Previous</span>
            </button>
            
            <div class="flex items-center space-x-1">
              <span class="text-sm text-gray-700">Page</span>
              <span class="font-medium">{{pagination.page}}</span>
              <span class="text-sm text-gray-700">of {{pagination.totalPages}}</span>
            </div>
            
            <button mat-button 
                    (click)="nextPage()"
                    [disabled]="pagination.page >= pagination.totalPages"
                    class="text-blue-600 hover:text-blue-800 disabled:text-gray-400">
              <span>Next</span>
              <mat-icon>chevron_right</mat-icon>
            </button>
          </div>
          
          <button mat-button 
                  (click)="loadCompanyAdmins()"
                  class="text-blue-600 hover:text-blue-800">
            <mat-icon>refresh</mat-icon>
            <span class="ml-1">Refresh</span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div *ngIf="!isLoading && displayAdmins.length === 0 && !errorMessage" class="text-center py-12">
    <mat-icon class="text-gray-400" style="font-size: 48px; width: 48px; height: 48px;">business</mat-icon>
    <h3 class="mt-4 text-lg font-medium text-gray-900">
      {{ isSearchActive ? 'No matching admins found' : 'No company admins found' }}
    </h3>
    <p class="mt-1 text-gray-500">
      {{ isSearchActive ? 'Try a different search term' : 'There are no company administrators available at the moment.' }}
    </p>
    <div class="mt-4 space-x-2">
      <button *ngIf="isSearchActive" 
              mat-button 
              (click)="clearSearch()"
              class="text-blue-600 hover:text-blue-800">
        Clear Search
      </button>
      <button mat-raised-button 
              color="primary" 
              (click)="openAddCompanyModal()"
              class="mt-2">
        <mat-icon>add</mat-icon>
        <span class="ml-1">Add Company Admin</span>
      </button>
    </div>
  </div>

<!-- Add Company Admin Modal -->
<div *ngIf="showAddCompanyModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
  <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
    <!-- Modal Header -->
    <div class="flex justify-between items-center p-6 border-b">
      <h2 class="text-xl font-semibold text-gray-900">Add New Company Admin</h2>
      <button mat-icon-button (click)="closeAddCompanyModal()" class="text-gray-400 hover:text-gray-600">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <!-- Modal Body -->
    <div class="p-6">
      <!-- Error Message -->
      <div *ngIf="modalErrorMessage" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <div class="flex items-center">
          <mat-icon class="text-red-500 mr-2" style="font-size: 18px;">error</mat-icon>
          <span class="text-red-700 text-sm">{{modalErrorMessage}}</span>
        </div>
      </div>

      <!-- Form -->
      <div class="space-y-4">
        <!-- Admin Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Admin Name *</label>
          <input type="text" 
                 [(ngModel)]="newCompanyData.name"
                 placeholder="Enter admin's full name"
                 class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
        </div>

        <!-- Email -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input type="email" 
                 [(ngModel)]="newCompanyData.email"
                 placeholder="Enter admin's email"
                 class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
        </div>

        <!-- Company Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
          <input type="text" 
                 [(ngModel)]="newCompanyData.companyName"
                 placeholder="Enter company name"
                 class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="flex justify-end space-x-3 mt-6">
        <button mat-stroked-button 
                (click)="closeAddCompanyModal()"
                [disabled]="isCreating"
                class="px-4 py-2">
          Cancel
        </button>
        <button mat-raised-button 
                color="primary" 
                (click)="createNewCompany()"
                [disabled]="isCreating"
                class="px-4 py-2">
          <span class="inline-flex items-center justify-center gap-2">
            <mat-spinner *ngIf="isCreating" diameter="20"></mat-spinner>
            <span>{{isCreating ? 'Sending...' : 'Create and send invitation'}}</span>
          </span>
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Delete Confirmation Modal -->
<div *ngIf="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
  <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
    <!-- Modal Header -->
    <div class="flex justify-between items-center p-6 border-b">
      <h2 class="text-xl font-semibold text-gray-900">
        {{adminToDelete?.accountStatus === 'invited' ? 'Revoke invitation' : 'Delete Company Admin'}}
      </h2>
      <button mat-icon-button 
              (click)="closeDeleteModal()" 
              [disabled]="isDeleting"
              class="text-gray-400 hover:text-gray-600">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <!-- Modal Body -->
    <div class="p-6">
      <!-- Warning Icon -->
      <div class="flex justify-center mb-4">
        <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <mat-icon class="text-red-600 text-3xl">warning</mat-icon>
        </div>
      </div>

      <!-- Confirmation Message -->
      <div class="text-center mb-6">
        <h3 class="text-lg font-medium text-gray-900 mb-2">Are you sure?</h3>
        <p class="text-gray-600 mb-4">
          You are about to {{adminToDelete?.accountStatus === 'invited' ? 'revoke the invitation for' : 'delete the company admin'}}
          <span class="font-semibold text-gray-900">{{adminToDelete?.name}}</span>
        </p>
        <p class="text-sm text-gray-500">
          <ng-container *ngIf="adminToDelete?.accountStatus === 'invited'; else deleteWarning">
            The activation link will stop working and the pending account will be removed.
          </ng-container>
          <ng-template #deleteWarning>
            This action cannot be undone. This will permanently delete the admin account and all associated data.
          </ng-template>
        </p>
      </div>

      <!-- Admin Details Card -->
      <div *ngIf="adminToDelete" class="bg-gray-50 rounded-lg p-4 mb-6">
        <div class="grid grid-cols-2 gap-3">
          <div>
            <p class="text-xs text-gray-500">Admin Name</p>
            <p class="font-medium">{{adminToDelete.name}}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Email</p>
            <p class="font-medium truncate">{{adminToDelete.email}}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Company</p>
            <p class="font-medium">{{adminToDelete.company?.name || 'N/A'}}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Created</p>
            <p class="font-medium">{{formatDate(adminToDelete.createdAt)}}</p>
          </div>
        </div>
      </div>

      <!-- Warning Note -->
      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div class="flex items-start">
          <mat-icon class="text-yellow-600 mr-2 mt-0.5">info</mat-icon>
          <div class="text-sm text-yellow-700">
            <p class="font-medium mb-1">Important Note</p>
            <p>{{adminToDelete?.accountStatus === 'invited'
              ? 'Revoking removes the pending administrator, but keeps the newly created company.'
              : 'Deleting this admin will also remove their access to the company dashboard and all related permissions.'}}</p>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="flex justify-end space-x-3">
        <button mat-stroked-button 
                (click)="closeDeleteModal()"
                [disabled]="isDeleting"
                class="px-4 py-2">
          Cancel
        </button>
        <button mat-raised-button 
                color="warn" 
                (click)="confirmDelete()"
                [disabled]="isDeleting"
                class="px-4 py-2 flex items-center">
          <mat-spinner *ngIf="isDeleting" diameter="20" class="mr-2"></mat-spinner>
          <span>{{isDeleting
            ? (adminToDelete?.accountStatus === 'invited' ? 'Revoking...' : 'Deleting...')
            : (adminToDelete?.accountStatus === 'invited' ? 'Revoke invitation' : 'Delete Admin')}}</span>
        </button>
      </div>
    </div>
  </div>
</div>
